import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type FoundryAdminListItem,
  type FoundryDetail,
  type FoundryPartAdmin,
  type FoundryPartWrite,
  type FoundryWrite,
  type InletTypeOption,
} from '../lib/api';

// Mirrors the legacy `foundry_part` GetDimensionString{Grate,Opening} accessors:
// "W x H" when both present, else the diameter, else "invalid".
function dimensionString(width: number | null, height: number | null, diameter: number | null): string {
  if (width != null && height != null) return `${width} x ${height} "`;
  if (diameter != null) return `${diameter} "`;
  return 'invalid';
}

/**
 * "Manage Foundries and Precast Parts" — faithful port of the legacy
 * `manage/foundrylist.blade.php`: the report-console header, a Foundry
 * selector with Add-Part / Edit-Foundry / Create-Foundry actions plus a
 * CSV download, then a single DataTables-style parts table with paging.
 */
export function AdminFoundriesPage() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [foundryModal, setFoundryModal] = useState<'create' | 'edit' | null>(null);
  const [partModal, setPartModal] = useState<FoundryPartAdmin | 'new' | null>(null);
  const [viewPart, setViewPart] = useState<FoundryPartAdmin | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin-foundries'],
    queryFn: () => api.get<FoundryAdminListItem[]>('/api/v1/admin/foundries'),
  });

  // Default-select the first foundry once the list loads.
  useEffect(() => {
    if (selectedId == null && listQuery.data && listQuery.data.length > 0) {
      setSelectedId(listQuery.data[0].id);
    }
  }, [listQuery.data, selectedId]);

  const inletQuery = useQuery({
    queryKey: ['admin-inlet-types'],
    queryFn: () => api.get<InletTypeOption[]>('/api/v1/admin/foundries/inlet-types'),
  });

  const csvHref = selectedId != null ? `/api/v1/admin/foundries/${selectedId}/export.csv` : '#';

  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12">
          <ReportConsoleNav />

          {/* Foundry selector + actions — port of the legacy foundrylist controls */}
          <div className="row">
            <div className="col-12 col-lg-5 pt-2">
              <div className="form-floating">
                <select
                  id="foundry"
                  className="form-select mb-2"
                  value={selectedId ?? ''}
                  onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                >
                  {listQuery.data?.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <label htmlFor="foundry">Foundry</label>
              </div>
            </div>
            <div className="col-12 col-lg-5 pt-2">
              <button type="button" className="btn btn-primary p-2 mb-2"
                disabled={selectedId == null} onClick={() => setPartModal('new')}>
                <span>Add New Part To Foundry</span>
              </button>{' '}
              <button type="button" className="btn btn-secondary p-2 mb-2"
                disabled={selectedId == null} onClick={() => setFoundryModal('edit')}>
                <span>Edit Foundry</span>
              </button>{' '}
              <button type="button" className="btn btn-secondary p-2 mb-2"
                onClick={() => setFoundryModal('create')}>
                <span>Create Foundry</span>
              </button>
            </div>
            <div className="col-12 col-lg-2 pt-2">
              <a className={`btn btn-primary p-2 mb-2${selectedId == null ? ' disabled' : ''}`}
                href={csvHref} download>
                <span>Download View As CSV</span>
              </a>
            </div>
          </div>

          {/* Parts table */}
          {listQuery.isLoading && <p className="text-muted">Loading…</p>}
          {listQuery.error && <p className="text-danger">Failed to load foundries.</p>}
          {listQuery.data && listQuery.data.length === 0 && (
            <p className="text-muted">No foundries yet. Use “Create Foundry” to add one.</p>
          )}
          {selectedId != null && (
            <FoundryPartsTable
              key={selectedId}
              foundryId={selectedId}
              onEditPart={(p) => setPartModal(p)}
              onViewPart={(p) => setViewPart(p)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {foundryModal && (
        <FoundryModal
          mode={foundryModal}
          foundryId={foundryModal === 'edit' ? selectedId : null}
          onClose={() => setFoundryModal(null)}
          onSaved={(id) => { setSelectedId(id); setFoundryModal(null); }}
        />
      )}
      {partModal && selectedId != null && (
        <ModalShell maxWidth={760} onClose={() => setPartModal(null)}>
          <PartEditor
            foundryId={selectedId}
            part={partModal === 'new' ? null : partModal}
            inletTypes={inletQuery.data ?? []}
            onClose={() => setPartModal(null)}
          />
        </ModalShell>
      )}
      {viewPart && <PartViewModal part={viewPart} onClose={() => setViewPart(null)} />}
    </div>
  );
}

// Link-based variant of the report-console header (the metrics pages use a
// button/tab variant). Rendered atop the foundries console exactly like the
// legacy `metrics.headernav` include — all icons grey since no report is active.
function ReportConsoleNav() {
  const cell = (to: string, label: string, icon: string) => (
    <div className="col-3 text-center border-bottom" style={{ flex: '1.7 0 0%' }}>
      <Link className="link-dark fs-6 text-decoration-none d-inline-flex align-items-center justify-content-center gap-2" to={to}>
        <img src={icon} alt="" style={{ height: 30, width: 'auto' }} /> {label}
      </Link>
    </div>
  );
  return (
    <div className="container-fluid p-0 mb-3">
      <div className="row mt-2 gx-0">
        <div className="col-3 text-center border-bottom">
          <Link className="link-dark fs-6 text-decoration-none d-inline-flex align-items-center justify-content-center gap-2" to="/">
            <img src="/Admin/Icon-Top-Grey-Home.png" alt="" style={{ height: 30, width: 'auto' }} /> RETURN HOME
          </Link>
        </div>
        {cell('/admin/console#users-report', 'USERS REPORTS', '/Admin/Icon-Top-Grey-Users.png')}
        {cell('/admin/console#projects-report', 'PROJECT REPORTS', '/Admin/Icon-Top-Grey-Reports.png')}
        {cell('/admin/console#structures-report', 'STRUCTURES', '/Admin/Icon-Top-Grey-Reports.png')}
        {cell('/admin/console#advanced-report', 'ADVANCED REPORTS', '/Admin/Icon-Top-Grey-Advanced.png')}
      </div>
    </div>
  );
}

// Simple centered modal backdrop. The content supplies its own header/close.
function ModalShell({ children, onClose, maxWidth = 720 }: {
  children: ReactNode;
  onClose: () => void;
  maxWidth?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1060,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto', padding: '3rem 1rem',
      }}
      onClick={onClose}
    >
      <div className="bg-white rounded shadow" style={{ width: '100%', maxWidth }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// Create/Edit foundry — replaces the legacy `manage/edit/foundry` page.
function FoundryModal({ mode, foundryId, onClose, onSaved }: {
  mode: 'create' | 'edit';
  foundryId: number | null;
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['admin-foundry', foundryId],
    queryFn: () => api.get<FoundryDetail>(`/api/v1/admin/foundries/${foundryId}`),
    enabled: mode === 'edit' && foundryId != null,
  });

  const [name, setName] = useState('');
  const [hide, setHide] = useState(false);
  const [hiddenReason, setHiddenReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && detailQuery.data) {
      setName(detailQuery.data.name);
      setHide(detailQuery.data.hidden);
      setHiddenReason(detailQuery.data.hiddenReason ?? '');
    }
  }, [mode, detailQuery.data]);

  const save = useMutation({
    mutationFn: () => {
      const body: FoundryWrite = { name: name.trim(), hide, hiddenReason: hide ? hiddenReason : null };
      return mode === 'edit' && foundryId != null
        ? api.put<FoundryDetail>(`/api/v1/admin/foundries/${foundryId}`, body)
        : api.post<FoundryDetail>('/api/v1/admin/foundries', body);
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['admin-foundries'] });
      if (foundryId != null) qc.invalidateQueries({ queryKey: ['admin-foundry', foundryId] });
      onSaved(saved.id);
    },
    onError: (e) => setError(e instanceof ApiError ? String(e.body) : 'Failed to save foundry.'),
  });

  return (
    <ModalShell maxWidth={520} onClose={onClose}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{mode === 'edit' ? 'Edit Foundry' : 'Create Foundry'}</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        <div className="mb-3">
          <label className="form-label small mb-1">Name</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-check mb-2">
          <input type="checkbox" className="form-check-input" id="hide-foundry"
            checked={hide} onChange={(e) => setHide(e.target.checked)} />
          <label className="form-check-label" htmlFor="hide-foundry">Hide this foundry</label>
        </div>
        {hide && (
          <div className="mb-3">
            <label className="form-label small mb-1">Hidden reason</label>
            <input className="form-control" value={hiddenReason}
              onChange={(e) => setHiddenReason(e.target.value)} placeholder="Why is this foundry hidden?" />
          </div>
        )}
        {error && <div className="text-danger small mb-2">{error}</div>}
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-primary"
            disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {mode === 'edit' ? 'Save Foundry' : 'Create Foundry'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </ModalShell>
  );
}

// Read-only part details — replaces the legacy `foundrypartview` page (eye icon).
function PartViewModal({ part, onClose }: { part: FoundryPartAdmin; onClose: () => void }) {
  const row = (label: string, value: ReactNode) => (
    <div className="row g-0 border-bottom py-1">
      <div className="col-5 text-muted small">{label}</div>
      <div className="col-7 small">{value || '—'}</div>
    </div>
  );
  return (
    <ModalShell maxWidth={520} onClose={onClose}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Part {part.foundryPartNumber}</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        {row('Custom', part.partId == null ? 'Yes' : 'No')}
        {row('Foundry Part Number', part.foundryPartNumber)}
        {row('Temporary', part.partNumberTemporary)}
        {row('Permanent No Filter', part.partNumberPermanentNoFilter)}
        {row('Permanent Oil Filter', part.partNumberPermanentOilFilter)}
        {row('Dimension Grate', dimensionString(part.grateSizeWidth, part.grateSizeHeight, part.grateSizeDiameter))}
        {row('Dimension Opening', dimensionString(part.openingSizeWidth, part.openingSizeHeight, part.openingSizeDiameter))}
        {row('Inlet Type', part.inletType)}
        {row('Line Note', part.lineNote)}
        {row('Hidden', part.hidden ? (part.hiddenReason || 'Yes') : 'No')}
        <div className="mt-3 text-end">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </ModalShell>
  );
}

// Single full-width DataTables-style parts table — port of the legacy
// `#userlist` DataTable: Show-entries length menu, search, paging and the
// per-row edit / disable-restore / view icon actions.
function FoundryPartsTable({ foundryId, onEditPart, onViewPart }: {
  foundryId: number;
  onEditPart: (p: FoundryPartAdmin) => void;
  onViewPart: (p: FoundryPartAdmin) => void;
}) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['admin-foundry', foundryId],
    queryFn: () => api.get<FoundryDetail>(`/api/v1/admin/foundries/${foundryId}`),
  });

  const hidePart = useMutation({
    mutationFn: (vars: { partId: number; hide: boolean; reason: string | null }) =>
      api.post<FoundryPartAdmin>(`/api/v1/admin/foundries/${foundryId}/parts/${vars.partId}/hide`, {
        hide: vars.hide, hiddenReason: vars.reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-foundry', foundryId] });
      qc.invalidateQueries({ queryKey: ['admin-foundries'] });
    },
  });

  const [search, setSearch] = useState('');
  const [pageLength, setPageLength] = useState(50);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, pageLength, foundryId]);

  const allParts = detailQuery.data?.parts ?? [];
  const term = search.trim().toLowerCase();
  const rows = term
    ? allParts.filter((p) =>
        [p.foundryPartNumber, p.partNumberTemporary, p.partNumberPermanentNoFilter,
          p.partNumberPermanentOilFilter, p.inletType]
          .some((v) => (v ?? '').toLowerCase().includes(term)))
    : allParts;

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageLength));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageLength;
  const pageRows = rows.slice(startIdx, startIdx + pageLength);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + pageLength, total);
  const pageNumbers = (() => {
    const out: number[] = [];
    const lo = Math.max(1, currentPage - 2);
    const hi = Math.min(totalPages, currentPage + 2);
    for (let p = lo; p <= hi; p++) out.push(p);
    return out;
  })();

  function toggleHide(p: FoundryPartAdmin) {
    if (p.hidden) { hidePart.mutate({ partId: p.id, hide: false, reason: null }); return; }
    const reason = window.prompt(`Hide part ${p.foundryPartNumber}? Optionally enter a reason:`, '');
    if (reason === null) return; // cancelled
    hidePart.mutate({ partId: p.id, hide: true, reason: reason || null });
  }

  if (detailQuery.isLoading) return <p className="text-muted mt-3">Loading…</p>;
  if (detailQuery.error || !detailQuery.data) return <p className="text-danger mt-3">Failed to load foundry parts.</p>;

  return (
    <div className="row mt-3 justify-content-center">
      <div className="col-12">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <label className="d-flex align-items-center gap-1 mb-0 text-muted small">
            Show
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={pageLength} onChange={(e) => setPageLength(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
          <label className="d-flex align-items-center gap-2 mb-0 text-muted small">
            Search:
            <input className="form-control form-control-sm" style={{ width: 220 }}
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
        </div>
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle mb-0 w-100">
            <thead>
              <tr>
                <th>Custom</th>
                <th>Foundry Part Number</th>
                <th>Temporary</th>
                <th>Permanent No Filter</th>
                <th>Permanent Oil Filter</th>
                <th>Dimension Grate</th>
                <th>Dimension Opening</th>
                <th>Inlet Type</th>
                <th>Hidden</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p) => (
                <tr key={p.id}>
                  <td className="small">{p.partId == null ? 'Yes' : 'No'}</td>
                  <td>{p.foundryPartNumber}</td>
                  <td className="small">{p.partNumberTemporary || ''}</td>
                  <td className="small">{p.partNumberPermanentNoFilter || ''}</td>
                  <td className="small">{p.partNumberPermanentOilFilter || ''}</td>
                  <td className="small text-nowrap">{dimensionString(p.grateSizeWidth, p.grateSizeHeight, p.grateSizeDiameter)}</td>
                  <td className="small text-nowrap">{dimensionString(p.openingSizeWidth, p.openingSizeHeight, p.openingSizeDiameter)}</td>
                  <td className="small">{p.inletType || ''}</td>
                  <td className="small">{p.hidden ? (p.hiddenReason || 'Hidden') : ''}</td>
                  <td className="p-0 pt-1 text-nowrap">
                    <button type="button" className="btn btn-link p-0" title="Edit part" onClick={() => onEditPart(p)}>
                      <img src="/Admin/Icon_Edit_Outlined.png" alt="Edit" style={{ maxHeight: 28 }} />
                    </button>
                    <button type="button" className="btn btn-link p-0 ms-1"
                      title={p.hidden ? 'Restore part' : 'Disable part'}
                      disabled={hidePart.isPending} onClick={() => toggleHide(p)}>
                      <img src={p.hidden ? '/Admin/Icon-Restore.png' : '/Admin/Icon-Suspend.png'}
                        alt={p.hidden ? 'Restore' : 'Disable'} style={{ maxHeight: 28 }} />
                    </button>
                    <button type="button" className="btn btn-link p-0 ms-1 me-1" title="View part" onClick={() => onViewPart(p)}>
                      <img src="/Admin/Icon-Info.png" alt="View" style={{ maxHeight: 28 }} />
                    </button>
                  </td>
                </tr>
              ))}
              {total === 0 && (
                <tr><td colSpan={10} className="text-center text-muted py-4">No data available in table</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2">
          <span className="text-muted small">Showing {showingFrom} to {showingTo} of {total} entries</span>
          <nav>
            <ul className="pagination pagination-sm pagination-rounded mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button type="button" className="page-link" aria-label="Previous"
                  onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
                  <i className="mdi mdi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((p) => (
                <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                  <button type="button" className="page-link" onClick={() => setPage(p)}>{p}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button type="button" className="page-link" aria-label="Next"
                  onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>
                  <i className="mdi mdi-chevron-right" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
}

function PartEditor({
  foundryId,
  part,
  inletTypes,
  onClose,
}: {
  foundryId: number;
  part: FoundryPartAdmin | null;
  inletTypes: InletTypeOption[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const initialInletId = useMemo(
    () => (part ? inletTypes.find((i) => i.name === part.inletType)?.id ?? null : null),
    [part, inletTypes],
  );

  const [form, setForm] = useState<FoundryPartWrite>({
    foundryPartNumber: part?.foundryPartNumber ?? '',
    partNumberTemporary: part?.partNumberTemporary ?? null,
    partNumberPermanentNoFilter: part?.partNumberPermanentNoFilter ?? null,
    partNumberPermanentOilFilter: part?.partNumberPermanentOilFilter ?? null,
    grateSizeWidth: part?.grateSizeWidth ?? null,
    grateSizeHeight: part?.grateSizeHeight ?? null,
    grateSizeDiameter: part?.grateSizeDiameter ?? null,
    openingSizeWidth: part?.openingSizeWidth ?? null,
    openingSizeHeight: part?.openingSizeHeight ?? null,
    openingSizeDiameter: part?.openingSizeDiameter ?? null,
    inletTypeId: initialInletId,
    lineNote: part?.lineNote ?? null,
  });
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FoundryPartWrite>(k: K, v: FoundryPartWrite[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k: keyof FoundryPartWrite, v: string) =>
    set(k, (v === '' ? null : Number(v)) as FoundryPartWrite[typeof k]);

  const mutation = useMutation({
    mutationFn: () =>
      part
        ? api.put<FoundryPartAdmin>(`/api/v1/admin/foundries/${foundryId}/parts/${part.id}`, form)
        : api.post<FoundryPartAdmin>(`/api/v1/admin/foundries/${foundryId}/parts`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-foundry', foundryId] });
      qc.invalidateQueries({ queryKey: ['admin-foundries'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? String(e.body) : 'Failed to save part.'),
  });

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{part ? `Edit Part ${part.foundryPartNumber}` : 'New Part'}</h6>
        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
      </div>
      <div className="row g-2">
        <div className="col-12">
          <label className="form-label small mb-1">Foundry Part Number *</label>
          <input className="form-control form-control-sm" value={form.foundryPartNumber}
            onChange={(e) => set('foundryPartNumber', e.target.value)} />
        </div>

        <div className="col-12">
          <label className="form-label small mb-1">Part Number Temporary</label>
          <input className="form-control form-control-sm" value={form.partNumberTemporary ?? ''}
            onChange={(e) => set('partNumberTemporary', e.target.value || null)} />
        </div>
        <div className="col-12">
          <label className="form-label small mb-1">Part Number Permanent No Filter</label>
          <input className="form-control form-control-sm" value={form.partNumberPermanentNoFilter ?? ''}
            onChange={(e) => set('partNumberPermanentNoFilter', e.target.value || null)} />
        </div>
        <div className="col-12">
          <label className="form-label small mb-1">Part Number Permanent Oil Filter</label>
          <input className="form-control form-control-sm" value={form.partNumberPermanentOilFilter ?? ''}
            onChange={(e) => set('partNumberPermanentOilFilter', e.target.value || null)} />
        </div>

        <div className="col-12">
          <label className="form-label small mb-1">Inlet Type</label>
          <select className="form-select form-select-sm" value={form.inletTypeId ?? ''}
            onChange={(e) => set('inletTypeId', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— none —</option>
            {inletTypes.map((i) => (
              <option key={i.id} value={i.id}>{i.name} ({i.abbreviation})</option>
            ))}
          </select>
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold mt-2 mb-1">Grate Size</label>
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Grate Size Width (Inches in decimal)</label>
          <input type="number" step="any" className="form-control form-control-sm" value={form.grateSizeWidth ?? ''}
            onChange={(e) => setNum('grateSizeWidth', e.target.value)} />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Grate Size Height (Inches in decimal)</label>
          <input type="number" step="any" className="form-control form-control-sm" value={form.grateSizeHeight ?? ''}
            onChange={(e) => setNum('grateSizeHeight', e.target.value)} />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Grate Size Diameter (Inches in decimal)</label>
          <input type="number" step="any" className="form-control form-control-sm" value={form.grateSizeDiameter ?? ''}
            onChange={(e) => setNum('grateSizeDiameter', e.target.value)} />
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold mt-2 mb-1">Opening Size</label>
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Opening Size Width (Inches in decimal)</label>
          <input type="number" step="any" className="form-control form-control-sm" value={form.openingSizeWidth ?? ''}
            onChange={(e) => setNum('openingSizeWidth', e.target.value)} />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Opening Size Height (Inches in decimal)</label>
          <input type="number" step="any" className="form-control form-control-sm" value={form.openingSizeHeight ?? ''}
            onChange={(e) => setNum('openingSizeHeight', e.target.value)} />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Opening Size Diameter (Inches in decimal)</label>
          <input type="number" step="any" className="form-control form-control-sm" value={form.openingSizeDiameter ?? ''}
            onChange={(e) => setNum('openingSizeDiameter', e.target.value)} />
        </div>

        <div className="col-12">
          <label className="form-label small mb-1 mt-2">Line Note</label>
          <input className="form-control form-control-sm" value={form.lineNote ?? ''}
            onChange={(e) => set('lineNote', e.target.value || null)} />
        </div>
      </div>

      {error && <div className="text-danger small mt-2">{error}</div>}
      <div className="mt-3 d-flex gap-2">
        <button type="button" className="btn btn-success btn-sm"
          disabled={!form.foundryPartNumber.trim() || mutation.isPending}
          onClick={() => mutation.mutate()}>
          Save Part
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
